import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import { getIn } from 'final-form';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { AvailabilityActionFormUtility } from 'router/subrouters/DataRoom/models/DataRoomBulk';
import { updateCheckboxValue } from '../../../../checkboxUtils';
import CheckIcon from 'icons/Check.icon';
import { WATERMARK_FIELD_NAMES } from '../FileSecurityLevels/useSelectLevel';

import './WatermarkChecklistFields.styles.scss';

const isTrue = ({ checked, isMixed }: AvailabilityActionFormUtility = {}) =>
  checked || isMixed;

type WatermarkChecklistFieldsProps = {
  disabled?: boolean;
};

const WatermarkChecklistFields: React.FC<WatermarkChecklistFieldsProps> = (
  props,
) => {
  const { disabled } = props;

  const classes = classNames('watermark-checklist-fields');

  const { t } = useTranslation();

  const { values, errors } = useFormState();
  const { change } = useForm();

  const fieldName = (base: string) => (disabled ? `${base}Placeholder` : base);

  const watermarkDiagonal = getIn(values, fieldName('watermarkDiagonal'));
  const watermarkHeader = getIn(values, fieldName('watermarkHeader'));
  const watermarkFooter = getIn(values, fieldName('watermarkFooter'));
  const watermarkFullName = getIn(values, fieldName('watermarkFullName'));
  const watermarkEmail = getIn(values, fieldName('watermarkEmail'));
  const watermarkIpAddress = getIn(values, fieldName('watermarkIpAddress'));

  const canShowVisibleWatermark =
    !disabled &&
    (isTrue(watermarkFullName) ||
      isTrue(watermarkEmail) ||
      isTrue(watermarkIpAddress));

  const isVisibleWatermarkPlacementSelected =
    isTrue(watermarkDiagonal) ||
    isTrue(watermarkHeader) ||
    isTrue(watermarkFooter);

  useEffect(() => {
    if (disabled || canShowVisibleWatermark) return;

    WATERMARK_FIELD_NAMES.forEach((name) => {
      change(`${name}.checked`, false);
      change(`${name}.isMixed`, false);
    });
  }, [canShowVisibleWatermark, disabled, change]);

  return (
    <div className={classes}>
      <div>
        <p className="checklist-item">
          <CheckIcon />
          <span>{t('watermarkCanInclude')}</span>
        </p>
        <Field
          component={CheckboxField}
          label={t('name')}
          name={`${fieldName('watermarkFullName')}.checked`}
          type="checkbox"
          disabled={disabled}
          className={classNames({ mixed: watermarkFullName?.isMixed })}
          onChange={(checked: boolean) =>
            updateCheckboxValue({
              name: fieldName('watermarkFullName'),
              checked,
              state: watermarkFullName?.state,
              isMixed: watermarkFullName?.isMixed,
              canDowngrade: true,
              onChange: change,
            })
          }
        />
        <Field
          component={CheckboxField}
          label={t('email')}
          name={`${fieldName('watermarkEmail')}.checked`}
          type="checkbox"
          disabled={disabled}
          className={classNames({ mixed: watermarkEmail?.isMixed })}
          onChange={(checked: boolean) =>
            updateCheckboxValue({
              name: fieldName('watermarkEmail'),
              checked,
              state: watermarkEmail?.state,
              isMixed: watermarkEmail?.isMixed,
              canDowngrade: true,
              onChange: change,
            })
          }
        />
        <Field
          component={CheckboxField}
          label={t('ipAddressAndTimestamp')}
          name={`${fieldName('watermarkIpAddress')}.checked`}
          type="checkbox"
          disabled={disabled}
          className={classNames({ mixed: watermarkIpAddress?.isMixed })}
          onChange={(checked: boolean) =>
            updateCheckboxValue({
              name: fieldName('watermarkIpAddress'),
              checked,
              state: watermarkIpAddress?.state,
              isMixed: watermarkIpAddress?.isMixed,
              canDowngrade: true,
              onChange: change,
            })
          }
        />
        <Field name={fieldName('watermarkFullName')} component="input" hidden />
        <Field name={fieldName('watermarkEmail')} component="input" hidden />
        <Field
          name={fieldName('watermarkIpAddress')}
          component="input"
          hidden
        />
      </div>

      <div>
        <p className="checklist-item">
          <CheckIcon />
          <span>{t('visibleWatermarkIncluded')}</span>
        </p>
        <Field
          component={CheckboxField}
          label={t('watermarkOptionDiagonal')}
          name={`${fieldName('watermarkDiagonal')}.checked`}
          type="checkbox"
          disabled={disabled || !canShowVisibleWatermark}
          className={classNames({ mixed: watermarkDiagonal?.isMixed })}
          onChange={(checked: boolean) =>
            updateCheckboxValue({
              name: fieldName('watermarkDiagonal'),
              checked,
              state: watermarkDiagonal?.state,
              isMixed: watermarkDiagonal?.isMixed,
              canDowngrade: true,
              onChange: change,
            })
          }
        />
        <Field
          component={CheckboxField}
          label={t('watermarkOptionHeader')}
          name={`${fieldName('watermarkHeader')}.checked`}
          type="checkbox"
          disabled={disabled || !canShowVisibleWatermark}
          className={classNames({ mixed: watermarkHeader?.isMixed })}
          onChange={(checked: boolean) =>
            updateCheckboxValue({
              name: fieldName('watermarkHeader'),
              checked,
              state: watermarkHeader?.state,
              isMixed: watermarkHeader?.isMixed,
              canDowngrade: true,
              onChange: change,
            })
          }
        />
        <Field
          component={CheckboxField}
          label={t('watermarkOptionFooter')}
          name={`${fieldName('watermarkFooter')}.checked`}
          type="checkbox"
          disabled={disabled || !canShowVisibleWatermark}
          className={classNames({ mixed: watermarkFooter?.isMixed })}
          onChange={(checked: boolean) =>
            updateCheckboxValue({
              name: fieldName('watermarkFooter'),
              checked,
              state: watermarkFooter?.state,
              isMixed: watermarkFooter?.isMixed,
              canDowngrade: true,
              onChange: change,
            })
          }
        />
        <Field name={fieldName('watermarkDiagonal')} component="input" hidden />
        <Field name={fieldName('watermarkHeader')} component="input" hidden />
        <Field name={fieldName('watermarkFooter')} component="input" hidden />

        {canShowVisibleWatermark && !isVisibleWatermarkPlacementSelected && (
          <Field
            name="watermarkPlacement"
            hidden
            component="input"
            validate={() => t('requiredField')}
          />
        )}

        {errors?.watermarkPlacement && (
          <span className="error">{errors.watermarkPlacement}</span>
        )}
      </div>
    </div>
  );
};

export default WatermarkChecklistFields;
